import { useEffect, useMemo, useState } from "react";

import type { Market, Order, Token, Trade, WalletTier } from "../api/types";
import { useTrades } from "../hooks/useTrades";
import { formatTime, shortId } from "../lib/format";
import { parseOr } from "../lib/money";
import { issuerLabel, notional } from "../lib/order";
import { accountLabel } from "../lib/accounts";
import { fillTier, mergeTrades, orderFillers } from "../lib/trades";
import { closedOrders, isTerminal, openOrders } from "../store/orders";
import { useAccounts } from "../store/useAccounts";
import { useConn } from "../store/useConn";
import { allFills, fillsOfMarket, useFills } from "../store/useFills";
import { cancelEverywhere, useOrders } from "../store/useOrders";
import { useTiers } from "../store/useTiers";
import { Empty, Panel } from "./Panel";
import { PositionSummary } from "./PositionSummary";
import { Counterparty } from "./Tier";

/**
 * Открытые ордера оператора.
 *
 * Список гидрируется из REST при монтировании и на КАЖДОМ переподключении
 * сокета. Это не оптимизация: web_api переигрывает новому клиенту историю
 * только стрима evt.tape, а evt.orders мостится живьём и не переигрывается
 * вовсе — значит, всё, что случилось с ордерами за время разрыва, до вкладки не
 * доедет никогда, и без гидрации список врал бы до перезагрузки страницы.
 */
interface Props {
  /** null — все рынки (дашборд), иначе только этот. */
  conditionId: string | null;
  /**
   * Токены рынка — ради исхода в строке ордера («BUY UP») и секции позиции: она
   * подписывает исходы их именами и выстраивает строки в порядке
   * outcome_index, том же, что у лесенок. На дашборде секции нет, а токены
   * приходят всех известных рынков — только ради исхода.
   */
  tokens?: Token[] | undefined;
  /**
   * Рынок — ради ДЕЙСТВИЯ, а не показа: чтобы разрешить досрочный выход тем же
   * кодом, которым живут кнопки продажи, нужны шаг цены, минимальный размер и
   * расписание комиссии. Показ обходится без него и на дашборде остаётся целым.
   */
  market?: Market | undefined;
}

export function OrdersPanel({ conditionId, tokens, market }: Props) {
  const state = useOrders();
  const generation = useConn((s) => s.generation);
  const connection = useConn((s) => s.connection);

  useEffect(() => {
    void useOrders.getState().hydrate();
    // generation в зависимостях — то же восстановление после разрыва, что у
    // стаканов в MarketPage, и по родственной причине.
  }, [generation, connection]);

  /**
   * Сделки того же рынка — ради контрагентов в строках.
   *
   * ДВА источника, и оба обязательны. Журнал (а не лента) отвечает за прошлое:
   * лента держит скользящее окно на пару минут, и ордер, исполненный десять
   * минут назад, остался бы без контрагента именно тогда, когда его и
   * разбирают. Ключ тот же, что у страницы сделок, — TanStack дедуплицирует.
   *
   * Но журнал спрашивается опросом раз в полминуты, а разбирают исполнение в
   * первые же секунды: свежий `MATCHED` стоял с прочерком и подсказкой «либо не
   * доехало, либо мейкером были мы», хотя сделка со всеми встречными строками
   * уже лежала в сторе филлов — её кладёт туда dispatch сразу из сокета.
   * Поэтому живые филлы подмешиваются к ответу журнала (lib/trades.mergeTrades),
   * и контрагент появляется вместе с самим исполнением.
   */
  const trades = useTrades(conditionId ? { market: conditionId } : {});
  const fillsByMarket = useFills((s) => s.byMarket);
  const tradeList = useMemo(
    () =>
      mergeTrades(
        trades.data?.trades ?? [],
        conditionId
          ? fillsOfMarket(fillsByMarket, conditionId)
          : allFills(fillsByMarket),
      ),
    [trades.data, fillsByMarket, conditionId],
  );

  /**
   * Тиры на сейчас — для контрагента, которого мы встретили ВПЕРВЫЕ.
   *
   * Такой кошелёк приезжает в сделке без снимка тира: профиль считается уже
   * после неё, и квалификация приходит секундами позже сообщением
   * `profile_update`. Без стора строка так и осталась бы «неизвестен» до
   * перезагрузки вкладки — тем же дефектом, ради которого стор и заведён.
   */
  const tiers = useTiers((s) => s.tiers);

  /**
   * Исход по токену ордера. Без него «BUY» в строке не отвечает на главный
   * вопрос — на что ставка: обе ноги бинарного рынка покупаются одной и той же
   * стороной, и UP от DOWN отличались только ценой.
   */
  const outcomes = useMemo(
    () => new Map((tokens ?? []).map((t) => [t.asset_id, t.outcome] as const)),
    [tokens],
  );

  const open = openOrders(state, conditionId);
  const closed = closedOrders(state, conditionId).slice(0, 10);
  const pendingCount = Object.keys(state.pending).length;

  return (
    <Panel
      title="Мои ордера"
      subtitle={open.length > 0 ? `${open.length} открытых` : undefined}
      actions={<CancelAll conditionId={conditionId} disabled={false} />}
      /* Плотная карточка: панель уехала в колонку 420 пикселей рядом с ходом
         накопления, и отступы шапки там оплачены всё время работы с рынком. */
      dense
    >
      {state.hydrateError && (
        <p className="mb-3 text-xs text-[var(--color-sell)]">
          не удалось прочитать список: {state.hydrateError}
        </p>
      )}

      {open.length === 0 && closed.length === 0 ? (
        <Empty>нет ордеров</Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[var(--color-muted)]">
              <tr className="text-left">
                <th className="pr-2 pb-1 font-normal">время</th>
                {conditionId === null && (
                  <th className="pr-2 pb-1 font-normal">рынок</th>
                )}
                {/* Чей ордер. Слот, а не адрес: адрес funder'а на экране не
                    читается, а слот — то же имя, что в выборе цели. */}
                <th className="pr-2 pb-1 font-normal">аккаунт</th>
                <th className="pr-2 pb-1 font-normal">сторона</th>
                <th className="pr-2 pb-1 text-right font-normal">цена</th>
                <th className="pr-2 pb-1 text-right font-normal">размер</th>
                {/* Сумма стоит рядом с ценой и размером — своими сомножителями.
                    Оператор набирает ордер в долларах, а видел до сих пор только
                    share, и пересчитывать их в уме приходилось на каждой строке. */}
                <th
                  className="pr-2 pb-1 text-right font-normal"
                  title="цена × размер: сколько денег в заявке"
                >
                  сумма, $
                </th>
                {/* Исполнение, статус и контрагент — второй строкой ордера (см.
                    Row): в колонке 420 пикселей одиннадцать столбцов уводили
                    кнопку отмены за горизонтальную прокрутку (стенд multi
                    2026-10-07), и снять стоящую заявку было нечем. */}
                <th className="sticky right-0 bg-[var(--color-panel)] pb-1" />
              </tr>
            </thead>
            <tbody>
              {open.map((o) => (
                <Row
                  key={o.client_order_id}
                  order={o}
                  outcome={outcomes.get(o.asset_id)}
                  showMarket={conditionId === null}
                  trades={tradeList}
                  tiers={tiers}
                />
              ))}
              {closed.map((o) => (
                <Row
                  key={o.client_order_id}
                  order={o}
                  outcome={outcomes.get(o.asset_id)}
                  showMarket={conditionId === null}
                  trades={tradeList}
                  tiers={tiers}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Позиция и её итог — ЗДЕСЬ ЖЕ, а не отдельной карточкой: список
          отвечает «что я отправил», секция — «что из этого получилось», и это
          один вопрос, разложенный на два ответа. На дашборде её нет: счёт
          считается по ОКНУ, а там рынки все сразу. */}
      {conditionId !== null && tokens !== undefined && (
        <PositionSummary conditionId={conditionId} tokens={tokens} market={market} />
      )}

      {pendingCount > 0 && (
        <p className="mt-3 text-xs text-[var(--color-muted)]">
          команд в полёте: {pendingCount}
        </p>
      )}
      <PendingProblems />
    </Panel>
  );
}

function Row({
  order,
  outcome,
  showMarket,
  trades,
  tiers,
}: {
  order: Order;
  /** Исход токена ордера; нет — рынок вкладке неизвестен. */
  outcome: string | undefined;
  showMarket: boolean;
  trades: readonly Trade[];
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const terminal = isTerminal(order.status);
  const pending = order.status === "PENDING";
  // Причина отказа раскрывается НАЖАТИЕМ, а не подсказкой при наведении.
  //
  // Подсказка здесь уже была и не работала: браузер показывает её с задержкой,
  // на строке терминала перекрывает соседние, а текст у наших отказов длинный
  // («по цене 0.99 ордер не встанет в стакан, а купит по встречной заявке
  // 0.98 …»). Оператор видел ряд REJECTED со знаком вопроса, который выглядел
  // неактивным, — то есть ровно то состояние, когда система знает причину и не
  // говорит её.
  const [reasonOpen, setReasonOpen] = useState(false);
  // Строкой, а не всем составом: опрос состава раз в 5 с иначе перерисовывал
  // бы каждую строку таблицы.
  const accountText = useAccounts((s) => accountLabel(s.data, order.account));

  return (
    <>
      <tr
        className="border-t border-[var(--color-border)]"
        style={{ opacity: terminal ? 0.55 : pending ? 0.75 : 1 }}
      >
        <td className="num pr-2 py-1">
          {formatTime(order.created_at)}
          {/* Кем выставлен — рядом со временем и только когда это НЕ оператор.
            Иначе ряд одинаковых ордеров подряд не отвечает на первый же вопрос
            разбора: я это накликал или подсистема? У оператора метки нет
            намеренно — она стояла бы в каждой строке и не различала бы ничего. */}
          {order.issued_by !== "operator" && order.issued_by !== "" && (
            <span
              className="ml-1 text-[10px] text-[var(--color-muted)]"
              title={`ордер выставила подсистема «${order.issued_by}», а не оператор`}
            >
              {issuerLabel(order.issued_by)}
            </span>
          )}
        </td>
        {showMarket && (
          <td className="pr-2 py-1">{shortId(order.condition_id, 10)}</td>
        )}
        <td className="pr-2 py-1 text-[var(--color-muted)]" title={order.account ?? "строка до бэкфилла"}>
          {accountText}
        </td>
        <td
          className="whitespace-nowrap pr-2 py-1 font-medium"
          style={{
            color:
              order.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)",
          }}
          title={outcome ? `${order.side} ${outcome}` : "исход неизвестен: рынка нет в справочнике вкладки"}
        >
          {order.side}
          {/* Исход — ВЕРСАЛЬЮ, как на кнопках быстрых действий: оператор сверяет
              строку с тем, что нажал, и разное написание превращало бы сверку
              в перевод. */}
          {outcome && <span className="ml-1">{outcome.toUpperCase()}</span>}
          {/* Тип — мелко в той же ячейке и только не-GTC: он нужен для сверки,
              а не для чтения, GTC — умолчание формы и быстрых «M», а столбец и
              даже слово в каждой строке стоили места кнопке отмены. */}
          {order.order_type !== "GTC" && (
            <span className="ml-1 text-[10px] font-normal text-[var(--color-muted)]">{order.order_type}</span>
          )}
        </td>
        <td className="num pr-2 py-1 text-right">{order.price}</td>
        <td className="num pr-2 py-1 text-right">{order.size}</td>
        <td className="num pr-2 py-1 text-right" title={amountHint(order)}>
          {notional(order.price, order.size) ?? "—"}
        </td>
        {/* Отмена прибита к правому краю и занимает обе строки ордера: даже
            когда таблица шире колонки (рынок, длинный контрагент), прокрутка её
            не уносит — стоящую заявку снимают как раз в спешке. */}
        <td
          rowSpan={2}
          className="sticky right-0 bg-[var(--color-panel)] py-1 pl-2 text-right align-middle"
        >
          {!terminal && <CancelButton order={order} />}
        </td>
      </tr>
      <tr style={{ opacity: terminal ? 0.55 : pending ? 0.75 : 1 }}>
        <td colSpan={showMarket ? 7 : 6} className="pr-2 pb-1.5 text-[11px] text-[var(--color-muted)]">
          исполнено <span className="num text-[var(--color-fg)]">{order.size_matched}</span>
          <span className="mx-1.5">·</span>
          <span style={{ color: statusColor(order.status) }}>{order.status}</span>
          {order.reason && (
            <button
              type="button"
              onClick={() => setReasonOpen((v) => !v)}
              className="ml-1 rounded border border-[var(--color-border)] px-1 text-[10px] text-[var(--color-muted)] hover:text-white"
              title={reasonOpen ? "свернуть причину" : "показать причину"}
            >
              {reasonOpen ? "▾" : "почему"}
            </button>
          )}
          {/* Кто налил — только у исполненных: «MATCHED» без контрагента
              отвечает, ЧТО случилось, но не ПРОТИВ КОГО, а разбирают исполнение
              именно по второму. У неисполненного здесь сказать нечего. */}
          {parseOr(order.size_matched, 0) > 0 && (
            <>
              <span className="mx-1.5">·</span>
              <Fillers order={order} trades={trades} tiers={tiers} />
            </>
          )}
        </td>
      </tr>
      {reasonOpen && order.reason && (
        // Отдельной строкой во всю ширину, а не в ячейке: причина длиннее любой
        // колонки, и втиснутая в статус она либо обрежется, либо растянет таблицу.
        <tr className="border-t border-[var(--color-border)] bg-black/20">
          <td colSpan={13} className="px-3 py-1.5 text-[var(--color-muted)]">
            {order.reason}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Что означает сумма в строке — и чего она НЕ означает.
 *
 * Это номинал ЗАЯВКИ (цена × размер), а не потраченные деньги, и разойтись они
 * могут в обе стороны. Биржа отдаёт тейкеру улучшение цены: FAK на 8.06 share по
 * лимиту 0.62 возвращается исполненным на 8.19672 share по средней 0.6099 — те
 * же пять долларов, но другой размер и другая цена. Цены исполнения в контракте
 * ордера нет вовсе (есть только лимит), поэтому «сколько ушло на самом деле»
 * отсюда не выводится, и притворяться, что выводится, нельзя.
 */
function amountHint(order: Order): string {
  const asked = notional(order.price, order.size);
  const base = `${order.price} × ${order.size} = $${asked ?? "—"} — номинал заявки по лимитной цене`;
  const matched = Number(order.size_matched);
  if (!Number.isFinite(matched) || matched <= 0) return base;
  return (
    `${base}. Исполнено ${order.size_matched} share; ` +
    "цену исполнения биржа по ордеру не отдаёт, она видна в средней цене позиции"
  );
}

function statusColor(status: Order["status"]): string {
  switch (status) {
    case "MATCHED":
      return "var(--color-buy)";
    case "REJECTED":
      return "var(--color-sell)";
    case "PENDING":
      return "var(--color-muted)";
    default:
      return "inherit";
  }
}

/**
 * Снятие ОДНОГО ордера.
 *
 * Была серая «×» в углу строки — цель размером с символ, в которую на живом
 * рынке надо попасть с первого раза. Промах по ней стоит секунд, а снимают ордер
 * тогда, когда цена уже пошла не туда.
 *
 * Подтверждения нет намеренно, в отличие от массового снятия рядом: снятие
 * одного ордера обратимо тем же движением, каким он и выставлялся, а лишний шаг
 * стоил бы ровно того времени, ради которого кнопку и увеличили.
 */
function CancelButton({ order }: { order: Order }) {
  const [busy, setBusy] = useState(false);
  const cancel = useOrders((s) => s.cancel);

  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        // Аккаунт — владельца ордера, а не выбранный в панели: снять можно и
        // ордер другого аккаунта, и выбор цели здесь ни при чём.
        await cancel(order.client_order_id, order.account);
        setBusy(false);
      }}
      // ✕, а не слово «отмена»: ячейка прибита к правому краю и ложится поверх
      // таблицы, и кнопка в слово шириной закрывала колонку суммы (стенд
      // 2026-10-07). Тот же знак снимает ордера в быстрых действиях.
      className="rounded border border-[var(--color-sell)] bg-[var(--color-sell)]/15 px-1.5 py-0.5 text-xs font-medium text-[var(--color-sell)] hover:bg-[var(--color-sell)]/30 disabled:opacity-40"
      title="снять этот ордер"
      aria-label="снять этот ордер"
    >
      {busy ? "…" : "✕"}
    </button>
  );
}

/**
 * Массовое снятие — в одно нажатие.
 *
 * Подпись ОБЯЗАНА называть охват: две кнопки выглядят одинаково, а одна из них
 * закрывает оператору всю книгу. По той же причине снятие доступно ВСЕГДА и
 * никогда не выводится из содержимого списка — это последний рубеж при любом
 * расхождении списка с реальностью.
 *
 * Второго шага у него нет по той же причине, что и у снятия одного ордера
 * выше: команда денег не тратит, а возвращает — снимает из стакана уже стоящие
 * заявки. Нажимают её тогда, когда цена пошла не туда, и подтверждение стоило
 * бы ровно тех секунд, ради которых снятие и делают.
 */
function CancelAll({
  conditionId,
  disabled,
}: {
  conditionId: string | null;
  disabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Снимает у ВСЕХ аккаунтов (cancelEverywhere): общей цели у панели больше
  // нет, а аварийный тормоз, снимающий «то, что выбрано», оставлял бы в
  // стакане ордера соседнего аккаунта ровно тогда, когда цена пошла не туда.
  const label = conditionId ? "снять все по рынку · все аккаунты" : "снять ВСЕ ордера · все аккаунты";

  return (
    <span className="flex items-center gap-1.5">
      {error && (
        <span className="max-w-48 truncate text-xs text-[var(--color-sell)]" title={error}>
          {error}
        </span>
      )}
      <button
        type="button"
        disabled={disabled || busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          const res = await cancelEverywhere(conditionId);
          setBusy(false);
          if (!res.ok) setError(res.error);
          else if (res.partial.length > 0) setError(`не ушло: ${res.partial.join("; ")}`);
        }}
        className="text-xs text-[var(--color-muted)] hover:text-[var(--color-sell)] disabled:opacity-40"
      >
        {busy ? "снимаю…" : label}
      </button>
    </span>
  );
}

/**
 * Команды, не получившие ответа.
 *
 * Текст не врёт: команда durable в стриме cmd.orders и будет выполнена, когда
 * трейдер вернётся. Сказать «ордер не выставлен» значило бы спровоцировать
 * второй ордер на те же деньги.
 */
function PendingProblems() {
  const pending = useOrders((s) => s.pending);
  const stuck = Object.values(pending).filter((p) => p.timedOut);
  if (stuck.length === 0) return null;

  return (
    <p className="mt-3 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
      {stuck.length === 1
        ? "команда осталась"
        : `команд осталось: ${stuck.length}`}{" "}
      без подтверждения дольше 15 с. Она не потеряна: команда лежит в очереди{" "}
      <code className="num">cmd.orders</code> и будет выполнена, когда трейдер
      вернётся. Повторная отправка создаст ВТОРОЙ ордер.
    </p>
  );
}

/**
 * Кто налил по ордеру.
 *
 * Чипом с адресом и тиром, а не текстом: адрес ведёт на страницу кошелька, где
 * и профиль, и состояние выгрузки истории. Из строки ордера дотуда иначе не
 * добраться вовсе — «MATCHED» отвечает, что случилось, но не отвечает, против
 * кого.
 *
 * Прочерк у неисполненного ордера и у ордера БЕЗ контрагентов означает разное,
 * и это различие сохранено словами. Мейкерская сделка контрагентов не имеет по
 * построению: на проводе видны только мейкеры, и когда мейкером были мы, адреса
 * агрессора нет вовсе (ADR-0007 §1).
 *
 * Именно поэтому «тейкер скрыт» пишется СЛОВАМИ, а не прочерком, — теми же, что
 * в ленте и в истории сделок. Прежняя подсказка перечисляла обе причины разом
 * («либо не доехало, либо мейкером были мы»), и мейкерское исполнение читалось
 * как задержка: оператор ждал адреса, которого не будет никогда. Отличить одно
 * от другого можно ровно потому, что сама сделка теперь известна сразу — она
 * приходит живьём из сокета (см. mergeTrades выше).
 */
function Fillers({
  order,
  trades,
  tiers,
}: {
  order: Order;
  trades: readonly Trade[];
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const filled = parseOr(order.size_matched, 0) > 0;
  if (!filled) return <span className="text-[var(--color-muted)]">—</span>;

  const state = orderFillers(order.order_id, trades);

  // Агрессор, восстановленный из цепочки (ADR-0008): это и есть тот, кто снял
  // нашу заявку. Тир берётся из стора — снимка на момент сделки у него не
  // бывает, потому что на проводе его не было вовсе.
  if (state.kind === "taker") {
    return (
      <Counterparty
        address={state.address}
        tier={tiers[state.address]}
        title="снял нашу заявку; адрес восстановлен из квитанции транзакции"
      />
    );
  }

  if (state.kind === "maker") {
    return (
      <span
        className="text-[var(--color-muted)]"
        title={
          "Мы стояли в стакане, и нас снял агрессор. На проводе видны только мейкеры, " +
          "его адреса там нет вовсе — есть лишь ордер " +
          (state.takerOrderID ? shortId(state.takerOrderID, 14) : "") +
          ". Он восстанавливается из квитанции транзакции и появится через несколько " +
          "секунд; если не появился — смотрите ноты taker_* в /healthz трейдера."
        }
      >
        тейкер скрыт
      </span>
    );
  }

  if (state.kind === "unknown") {
    return (
      <span
        className="text-[var(--color-muted)]"
        title="сделка по ордеру ещё не доехала из приватного канала либо приехала без встречных строк"
      >
        —
      </span>
    );
  }

  // Показывается крупнейший: он и решает, что это была за сделка. Остальные не
  // прячутся молча — их число стоит рядом, как в ленте.
  const [main, ...rest] = state.fills;
  if (!main) return <span className="text-[var(--color-muted)]">—</span>;
  // Снимок тира на момент сделки важнее сегодняшнего, но у ПЕРВОЙ встречи с
  // кошельком снимка нет вовсе — тогда берётся то, что известно сейчас
  // (lib/trades.fillTier). Прежде здесь стоял только снимок, и незнакомый
  // контрагент оставался «неизвестен» навсегда, хотя тир доезжал секундами
  // позже.
  const { tier } = fillTier(main, tiers);
  return (
    <Counterparty address={main.address} tier={tier} extra={rest.length} />
  );
}
